import { useLayoutEffect, useRef, useState } from "react";

export interface RailTopic {
  id: string;
  name: string;
  /** Pastelized tab color. */
  color: string;
  /** Emoji glyph for the tab + sticker, or null. */
  icon: string | null;
  children: Array<{ id: string; name: string; color: string; icon: string | null }>;
}

interface Props {
  topics: RailTopic[];
  values: string[];
  onChange: (id: string) => void;
  subSel: string[];
  onToggleSub: (id: string) => void;
}

const CLOSE_MS = 500;

interface Widths { main?: number; sub?: number; subSel?: number }

/** Topic rail (left). Tab widths are measured from content so no label is ever
 *  clipped. All subtopics of a topic share the width of the longest one; the
 *  main topic always reaches a little further left than its widest subtopic. */
export function TopicRail({ topics, values, onChange, subSel, onToggleSub }: Props) {
  const [w, setW] = useState<Record<string, Widths>>({});
  const meas = useRef<Record<string, { main: HTMLElement | null; subs: Array<HTMLElement | null> }>>({});
  const selectedValues = new Set(values);
  const selectedSubValues = new Set(subSel);

  const setRef = (tid: string, key: "main" | "sub", i = 0) => (el: HTMLDivElement | null) => {
    if (!meas.current[tid]) meas.current[tid] = { main: null, subs: [] };
    if (key === "main") meas.current[tid]!.main = el;
    else meas.current[tid]!.subs[i] = el;
  };

  const remeasure = () => {
    const next: Record<string, Widths> = {};
    const MAIN_PAD = 26; // icon + gaps + L/R padding slack for the main tab
    const SUB_PAD = 22;
    const SUB_LEAD = 16; // selected subtopic reaches this much further left
    const MAIN_LEAD = 26; // main tab always clears the widest subtopic by this
    topics.forEach((t) => {
      const m = meas.current[t.id];
      if (!m) return;
      const mainContent = (m.main ? m.main.offsetWidth : 0) + MAIN_PAD;
      const subWidths = (m.subs || []).filter(Boolean).map((e) => e!.offsetWidth + SUB_PAD);
      const subBase = subWidths.length ? Math.max(...subWidths) : 0;
      const subSelW = subBase ? subBase + SUB_LEAD : 0;
      const mainW = Math.max(mainContent, subSelW + MAIN_LEAD);
      next[t.id] = { main: Math.ceil(mainW), sub: Math.ceil(subBase), subSel: Math.ceil(subSelW) };
    });
    setW(next);
  };

  useLayoutEffect(() => {
    remeasure();
    document.fonts?.ready?.then(remeasure).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics]);

  // When you leave a topic that has subtopics, HOLD its tab in the grown state
  // until the subtopics have finished retracting — only then does the main tab
  // shrink back. useLayoutEffect (not useEffect) so the is-closing class lands
  // before the deselect render paints; otherwise the tab flashes small.
  const [closingTabs, setClosingTabs] = useState<string[]>([]);
  const prevVals = useRef<string[]>(values);
  useLayoutEffect(() => {
    const prev = prevVals.current;
    prevVals.current = values;
    const next = new Set(values);
    const removed = prev.filter((id) => !next.has(id) && topics.find((t) => t.id === id)?.children.length);
    if (removed.length) {
      setClosingTabs((cur) => [...new Set([...cur, ...removed])]);
      const id = setTimeout(
        () => setClosingTabs((cur) => cur.filter((id) => !removed.includes(id))),
        CLOSE_MS + 120,
      );
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <div className="topic-rail">
      {topics.map((t) => {
        const selected = selectedValues.has(t.id);
        const open = selected && t.children.length > 0;
        const closing = !selected && closingTabs.includes(t.id);
        const dim = w[t.id] ?? {};
        return (
          <div key={t.id} className="topic-group">
            <div
              className={"topic-tab" + (selected ? " is-selected" : "") + (closing ? " is-closing" : "")}
              style={{
                ["--tcolor" as string]: t.color,
                ["--tab-w" as string]: dim.main ? dim.main + "px" : undefined,
              }}
              onClick={() => onChange(t.id)}
              title={t.name}
            >
              {t.icon && <span className="t-icon">{t.icon}</span>}
              <span className="t-name">{t.name}</span>
            </div>

            {t.children.length > 0 && (
              <div className={"subtopic-list" + (open ? " is-open" : "")}>
                <div className="subtopic-clip">
                  <div className="subtopic-list-inner">
                    {t.children.map((s, i) => {
                      const sSel = selectedSubValues.has(s.id);
                      return (
                        <div
                          key={s.id}
                          className={"subtopic-tab" + (sSel ? " is-selected" : "")}
                          data-sub-id={s.id}
                          style={{
                            ["--tcolor" as string]: s.color,
                            ["--sub-w" as string]: dim.sub ? dim.sub + "px" : undefined,
                            ["--sub-sel-w" as string]: dim.subSel ? dim.subSel + "px" : undefined,
                            ["--i" as string]: i,
                          }}
                          onClick={() => onToggleSub(s.id)}
                          title={s.name}
                        >
                          {s.icon && <span className="st-icon">{s.icon}</span>}
                          <span className="st-name">{s.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Hidden measurer — natural (max-content) widths for every label */}
      <div className="topic-measure" aria-hidden="true">
        {topics.map((t) => (
          <div key={t.id}>
            <div className="topic-tab is-selected" ref={setRef(t.id, "main")}>
              {t.icon && <span className="t-icon">{t.icon}</span>}
              <span className="t-name">{t.name}</span>
            </div>
            {t.children.map((s, i) => (
              <div key={s.id} className="subtopic-tab" ref={setRef(t.id, "sub", i)}>
                {s.icon && <span className="st-icon">{s.icon}</span>}
                <span className="st-name">{s.name}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
